import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useInfiniteQuery, useMutation } from "react-query"
import { useParams } from "react-router-dom"
import type { Node } from "reactflow"
import { Handle, Position, useReactFlow, useStoreApi } from "reactflow"

import BlockIcon from "@mui/icons-material/Block"
import CloseIcon from "@mui/icons-material/Close"
import { Card, CircularProgress, Grid, IconButton } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { InputText, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import type { Label } from "dagrejs"

import { ValueBlock } from "../../../../../components/data-display/ValueBlock"
import { LabelsMenu } from "../../../../../components/form/LabelsMenu"
import { getTheme } from "../../../../../hooks/UseTheme"
import { useDebounce } from "../../../../../hooks/useDebounce"
import { KonanAPI } from "../../../../../services/KonanAPI"
import { queryClient } from "../../../../../store/QueryClientStore"
import type { PaginatedLabelList } from "../../../../../types/generated/api/PaginatedLabelList"
import { PROHIBITED_LABELS_KEYWORDS } from "../../../../../utils/constants"
import { extractPageFromBackEndPaginationLink, isInputValueValid } from "../../../../../utils/genericHelpers"
import type { UpdateGraphParams } from "../../../types/interfaces"
import { handleDeletingNodeInBetween } from "../../../utils/workflowHelpers"

import styles from "../Nodes.module.scss"

const REASON_INPUT_CHAR_LIMIT = 50

interface LabeledNodeProps {
  menuView?: boolean
  data: {
    updateGraph?: (data: UpdateGraphParams) => void
    nodeType: "EndResultNode"
    readMode?: boolean
    liveModelState?: { name: string | null; disable: null | string }
    return_label?: Label
    description?: string
    hasChildren?: boolean
    isNodeInLoop?: boolean
  }
  id?: string
}

/**
 * Grouping component for (live model node + return label node)
 * @param {object} data and object contains all passed data to this node
 * @param {boolean} menuView an indicator if the current view is menu or not
 * @param {string} id node id
 * @return {React.ReactElement}
 */
export const LabeledNode = memo(function LabeledNode(props: Readonly<LabeledNodeProps>): React.ReactElement {
  // Prop destructuring
  const { menuView, data, id } = props

  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // Theme hook
  const theme = getTheme()

  // React Flow hooks
  // This is reactflow helper function to delete passed nodes as well as all the connected edges from the passed nodes
  const { getEdges } = useReactFlow()
  const store = useStoreApi()
  const { setNodes, getNodes } = store.getState()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "labeledNode",
  })

  const nodeId = id ?? null
  const readMode = data?.readMode ?? null
  const return_label = data?.return_label ?? null

  const pageSize = 20
  const [page, setPage] = useState<number>(0)
  const [searchKey, setSearchKey] = useState<string | null>(return_label?.name ?? null)

  const [labels, setLabels] = useState<Label[]>([])

  const [reasonInputValue, setReasonInputValue] = useState<string>(
    getNodes().find((node) => node.id === id)?.data?.description || "",
  )

  const CreateLabelMutation = useMutation<
    AxiosResponse<Label>,
    AxiosError,
    {
      project_uuid: string
      name: string
    }
  >(KonanAPI.CreateLabel, {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.labelCreation.success"), { severity: "success" })

      await queryClient.invalidateQueries("label-list")
    },
    onError: () => {
      NotificationUtils.toast(t("notifications.labelCreation.error"), { severity: "error" })
    },
  })

  // fetch project labels
  const {
    data: labelList,
    isLoading: isLabelListLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedLabelList>, AxiosError, Label>(
    ["label-list", projectId, page, pageSize, searchKey],
    ({ pageParam = 1 }) =>
      KonanAPI.RetrieveLabels({
        project_uuid: projectId as string,
        pageSize: pageSize,
        page: pageParam,
        search: searchKey,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<Label[]>((accumulator, page) => accumulator.concat(page.data.results as Label[]), []),
      }),
      onSuccess: (response) => {
        // filtering out already existing labels in the list
        const tempLabels =
          response.pages?.filter((item: Label) => !labels.find((label: Label) => label.uuid === item.uuid)) ?? []

        setLabels([...labels, ...tempLabels])

        response.pages?.length === 1 &&
          !["", null, undefined].includes(searchKey) &&
          response.pages[0] &&
          // setting name (automatically) ONLY if the user writes the full name correctly
          response.pages[0].name === searchKey &&
          setFlowNodes({
            name: response.pages[0].name,
            uuid: response.pages[0].uuid,
            description: reasonInputValue,
          })
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !menuView && !readMode && data.nodeType === "EndResultNode",
      refetchOnMount: true,
    },
  )

  const handleLabelChange = (e: InputChangeEvent): void => {
    e.preventDefault()

    setFlowNodes({
      name: e?.target?.value as string,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      uuid: ![null, undefined, ""].includes(e?.target?.value)
        ? labelList?.pages?.find((label) => label.name === e.target.value)?.uuid
        : "",
      description: reasonInputValue,
    })

    debouncedOnChange()
  }

  const reasonChangeHandler = (e: InputChangeEvent<Element>): void => {
    e.preventDefault()
    if (
      !("value" in e.currentTarget) ||
      typeof e.currentTarget.value !== "string" ||
      e.currentTarget.value.length > REASON_INPUT_CHAR_LIMIT
    )
      return
    const inputValue = e.currentTarget.value

    setReasonInputValue(inputValue)
    if (labelList && Array.isArray(labelList?.pages) && labelList?.pages.length > 0) {
      setFlowNodes({
        name: return_label?.name,
        uuid: return_label?.uuid,
        description: inputValue,
      })
    }
    debouncedOnChange()
  }

  const setFlowNodes = (props: { name: string; uuid: string; description: string }): void => {
    const { name, uuid, description } = props

    setNodes(
      getNodes().map((node) => {
        const newNode = { ...node }
        if (node.id === nodeId) {
          newNode.data = {
            ...newNode.data,
            return_label: {
              name: name,
              uuid: uuid,
            },
            description,
          }
        }
        return newNode
      }),
    )
  }

  const debouncedOnChange = useDebounce((): void => {
    const label = return_label?.name ?? ""

    setSearchKey(label === "" ? null : label)
    setPage(0)
  }, 400)

  /**
   * Our onNodesDelete callback is called with one argument - deleted -
   * that is an array of every node that was just deleted.
   * If you select an individual node and press the delete key, deleted will contain just that node,
   * but if you make a selection all the nodes in that selection will be in deleted.
   */
  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      const newNodesAndEdges = handleDeletingNodeInBetween(deleted, getEdges(), getNodes(), nodeId as string)

      if (data?.updateGraph) {
        data?.updateGraph({ dataAfterDeletion: { nodes: newNodesAndEdges?.nodes, edges: newNodesAndEdges?.edges } })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getEdges, getNodes, nodeId],
  )

  const handleRemoveNode = (): void => {
    if (data?.isNodeInLoop) {
      const deleted = getNodes().filter((node) => node.id === nodeId)

      onNodesDelete(deleted)
    } else {
      const newNodes = getNodes()?.map((node) => {
        const newNode = { ...node }

        if (node.id === nodeId) {
          newNode.type = "AddBlockNode"

          // Destructure the data object from newNode
          const { data } = newNode

          // Extract the desired properties from data
          const { updateGraph, parentId, formik, schema } = data

          // Assign the filtered data back to newNode
          newNode.data = { updateGraph, parentId, formik, schema }
        }

        return newNode
      })

      if (data?.updateGraph) {
        data?.updateGraph({ dataAfterDeletion: { nodes: newNodes, edges: [] } })
      }
    }

    NotificationUtils.toast(t("notifications.nodeRemoved"), {
      severity: "success",
    })
  }

  // memo to track input changes in editMode
  const inputValue = useMemo(() => {
    if (!readMode && data.nodeType === "EndResultNode") {
      getNodes().forEach((node) => {
        if (node.id === nodeId) {
          return node?.data?.return_label.name
        }
      })
    } else {
      return ""
    }
  }, [data.nodeType, getNodes, nodeId, readMode])

  const handleLabelSelection = (item: Label): void => {
    setFlowNodes({
      name: item.name,
      uuid: item.uuid,
      description: reasonInputValue,
    })
  }

  const handleAddNewLabel = async (labelName: string): Promise<void> => {
    if (isInputValueValid("label", labelName, PROHIBITED_LABELS_KEYWORDS)) {
      await CreateLabelMutation.mutateAsync({
        project_uuid: projectId as string,
        name: labelName,
      }).then((res) => {
        setFlowNodes({
          name: res.data.name,
          uuid: res.data.uuid,
          description: reasonInputValue,
        })
      })
    }
  }

  // creating a replica of formik touched
  // till we figure out if we should do this in KONAN or SUI
  const ref = useRef({ now: return_label?.name, prev: return_label?.name })
  useEffect(() => {
    ref.current.now = return_label?.name
  }, [return_label?.name])

  const nodesData = [
    {
      nodeTitle: t("nodeTitle"),
      nodeLogo: <BlockIcon sx={{ color: theme.palette.base.text[1] }} />,
      logoClassName: "endCardType",
      nodeBody: (
        <Grid item container xs={12} display="flex" flexWrap="nowrap">
          <Grid item xs={2.5} mr={1} marginTop={!readMode ? "6px" : "4px"}>
            <Typography variant="h3-bold" variantColor={menuView ? 2 : 1}>
              {t("nodeTitle")}
            </Typography>
          </Grid>
          <Grid item xs={readMode ? 9.5 : true} marginTop={readMode ? "2px" : "-4px"}>
            {readMode && return_label ? (
              <ValueBlock value={return_label.name} size={12} />
            ) : (
              <LabelsMenu
                filteredLabels={labels}
                value={inputValue ?? return_label?.name ?? ""}
                onChange={handleLabelChange}
                onSelectingLabel={handleLabelSelection}
                onAddingNewLabel={handleAddNewLabel}
                disabled={menuView}
                placeholder={t("labelPlaceholder")}
                onLoadMore={() => fetchNextPage()}
                hasMore={hasNextPage}
                isLoading={isLabelListLoading || isFetchingNextPage}
              />
            )}
          </Grid>
        </Grid>
      ),
      nodeReason: (
        <div className={styles.reasonWrapper}>
          <div className={styles.reasonHeaderWrapper}>
            <Typography variant="p">
              <Trans
                i18nKey={return_label?.name ? "labeledNode.reasonQuestion" : "labeledNode.noReason"}
                components={[<span />]}
                ns="workflows"
                values={{ labelName: return_label?.name }}
              />
            </Typography>
            <Typography variant="p" variantColor={2}>
              {t("optional")}
            </Typography>
          </div>
          {readMode && return_label ? (
            <ValueBlock value={reasonInputValue} size={12} />
          ) : (
            <InputText
              fullWidth
              required
              disabled={menuView}
              handleChange={reasonChangeHandler}
              value={reasonInputValue}
              hideDescription
              loaderComponent={
                <Grid container item xs={12} px={2} py={0.5}>
                  <CircularProgress size={14} />
                </Grid>
              }
              loading={CreateLabelMutation.isLoading}
              isLoadingOptions={isLabelListLoading}
              placeholder={t("inputPlaceholder")}
              type="text"
            />
          )}
          <Typography variant="p" variantColor={2}>
            {t("charCount", {
              currentCount: reasonInputValue.length,
              maxCount: REASON_INPUT_CHAR_LIMIT,
            })}
          </Typography>
        </div>
      ),
    },
  ]

  return (
    <Card className={`${styles.card} disableKeyboardA11y nodrag`}>
      {!menuView && <Handle style={{ opacity: 0 }} type="target" position={Position.Top} id="b" isConnectable={true} />}
      <Grid container justifyContent={"space-between"}>
        <Grid item xs={1.5}>
          <Grid item className={styles[`${nodesData[0]?.logoClassName}`]}>
            {nodesData[0]?.nodeLogo}
          </Grid>
        </Grid>

        <Grid
          item
          width="100%"
          xs={10.5}
          flexDirection={"column"}
          paddingLeft={1}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Grid item xs={12} container justifyContent="space-between">
            <Grid item>
              <Typography variant="label-bold" variantColor={2}>
                {nodesData[0]?.nodeTitle}
              </Typography>
            </Grid>

            {/** Close icon */}
            {!menuView && !readMode && (
              <Grid item marginRight={"-5px"} justifySelf={"end"}>
                <IconButton style={{ marginTop: "-15px" }} size="small" onClick={() => handleRemoveNode()}>
                  <CloseIcon fontSize="small" className={styles.remove} />
                </IconButton>
              </Grid>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            container
            flexDirection={"column"}
            gap={0.5}
            alignItems="space-between"
            justifyContent={"flex-end"}
            marginTop={!readMode && !menuView ? "0px" : menuView ? "6px" : "5px"}
          >
            <Grid item xs display="flex">
              {nodesData[0]?.nodeBody}
            </Grid>

            {!menuView && !readMode && data?.nodeType === "EndResultNode" && data?.isNodeInLoop && (
              <Grid item xs={12} className={styles.loopWarning}>
                <Typography gutterBottom variant="label-bold" variantColor={2} color="warning">
                  {t("warnings.loop.title")}
                </Typography>

                <Typography variant="p">{t("warnings.loop.description")}</Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        {nodesData[0]?.nodeReason}
      </Grid>

      {!menuView && data?.nodeType === "EndResultNode" && data?.isNodeInLoop && (
        <Handle style={{ opacity: 0 }} type="source" position={Position.Bottom} id="a" isConnectable={true} />
      )}
    </Card>
  )
})
